import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import StatusMarquee from "./StatusMarquee";

const Navbar = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const [mobileSearchModalOpen, setMobileSearchModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const searchRef = useRef(null);
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await apiService.request('/products/categories/active');
                if (response.status === 'success' && response.data.categories) {
                    const formattedCategories = response.data.categories.map(cat => ({
                        name: cat.name.split('-').map(word =>
                            word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' '),
                        slug: cat.name,
                        image: "https://placehold.co/200x200?text=IMAGE"
                    }));
                    setCategories(formattedCategories);
                }
            } catch (error) {
                console.error('Failed to fetch categories:', error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setSearchExpanded(false);
                setSearchResults([]);
                setSearchQuery("");
            }
        };

        if (searchExpanded) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [searchExpanded]);

    useEffect(() => {
        const searchProducts = async () => {
            if (searchQuery.trim().length < 2) {
                setSearchResults([]);
                return;
            }

            setIsSearching(true);
            try {
                const response = await apiService.searchProducts(searchQuery, { limit: 8 });
                if (response && response.data && response.data.products) {
                    setSearchResults(response.data.products);
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error("Search error:", error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        };

        const debounceTimer = setTimeout(() => {
            searchProducts();
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchQuery]);

    const handleSearchClick = () => {
        setSearchExpanded(true);
    };

    const handleProductClick = (productId) => {
        setSearchExpanded(false);
        setSearchResults([]);
        setSearchQuery("");
        navigate(`/product/${productId}`);
    };

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
            {/* Main navigation */}
            <div className="border-b border-gray-100">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 md:grid md:grid-cols-3">
                        {/* Logo */}
                        <motion.div
                            className="flex-shrink-0 md:justify-self-start flex items-center space-x-3"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6 }}
                        >
                            <Link to="/">
                                <img src="/logo.svg" alt="Kigo Prints" className="h-5 w-auto cursor-pointer" />
                            </Link>{" "}
                            {/* <span className="font-bold text-lg text-gray-800">KIGO PRINTS</span> */}
                        </motion.div>
                        {/* Navigation Links - Desktop - Centered */}
                        <motion.div
                            className="hidden md:flex items-center space-x-8 justify-self-center"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            {" "}
                            <motion.a
                                href="/"
                                className="transition-colors duration-300 font-medium text-gray-600 hover:text-[#008000]"
                                whileHover={{ scale: 1.05 }}
                            >
                                Home
                            </motion.a>{" "}
                            <motion.a
                                href="/#products"
                                className="transition-colors duration-300 font-medium text-gray-600 hover:text-[#008000]"
                                whileHover={{ scale: 1.05 }}
                            >
                                Products
                            </motion.a>
                            <Link to="/templates">
                                <motion.a
                                    className="transition-colors duration-300 font-medium text-gray-600 hover:text-[#008000]"
                                    whileHover={{ scale: 1.05 }}
                                >
                                    Templates
                                </motion.a>
                            </Link>
                            <motion.a
                                href="/about-us"
                                className="transition-colors duration-300 font-medium text-gray-600 hover:text-[#008000]"
                                whileHover={{ scale: 1.05 }}
                            >
                                Business
                            </motion.a>
                        </motion.div>{" "}
                        {/* Right Side Actions */}
                        <motion.div
                            className="flex items-center space-x-5 md:justify-self-end relative"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            {/* Search */}
                            <div className="relative flex items-center" ref={searchRef}>
                                {!searchExpanded ? (
                                    <motion.button
                                        className="transition-colors duration-300 hidden md:block p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-[#008000]"
                                        onClick={handleSearchClick}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                    </motion.button>
                                ) : (
                                    <motion.div
                                        className="hidden md:flex items-center"
                                        initial={{ width: 0, opacity: 0 }}
                                        animate={{ width: "200px", opacity: 1 }}
                                        exit={{ width: 0, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        style={{ minWidth: "200px" }}
                                    >
                                        <div className="relative w-full">
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                placeholder="Search products..."
                                                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#008000] focus:border-transparent bg-white h-10"
                                                autoFocus
                                            />
                                            <button
                                                onClick={() => {
                                                    setSearchExpanded(false);
                                                    setSearchResults([]);
                                                    setSearchQuery("");
                                                }}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 z-10"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                        {searchQuery.trim().length >= 2 && (
                                            <motion.div
                                                className="absolute top-full left-0 mt-2 w-full bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto z-50"
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                            >
                                                {isSearching ? (
                                                    <div className="p-4 text-center text-gray-500">Searching...</div>
                                                ) : searchResults.length > 0 ? (
                                                    <div>
                                                        {searchResults.map((product) => (
                                                            <button
                                                                key={product._id || product.id}
                                                                onClick={() => handleProductClick(product._id || product.id)}
                                                                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                                            >
                                                                {product.images && product.images[0] && (
                                                                    <img
                                                                        src={typeof product.images[0] === 'string' ? product.images[0] : (product.images[0].url || product.images[0])}
                                                                        alt={product.name}
                                                                        className="w-12 h-12 object-cover rounded aspect-square"
                                                                    />
                                                                )}
                                                                <div className="flex-1 text-left">
                                                                    <p className="font-medium text-gray-800">{product.name}</p>
                                                                    {product.pricing && product.pricing[0] && (
                                                                        <p className="text-sm text-gray-600">
                                                                            ₹{product.pricing[0].basePrice || product.pricing[0].price}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="p-4 text-center text-gray-500">No products found</div>
                                                )}
                                            </motion.div>
                                        )}
                                    </motion.div>
                                )}
                            </div>
                            {/* Mobile Search Icon */}
                            <motion.button
                                className="transition-colors duration-300 md:hidden p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-[#008000]"
                                onClick={() => setMobileSearchModalOpen(true)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                    />
                                </svg>
                            </motion.button>
                            <motion.button
                                className="bg-[#008000] text-white hover:bg-[#008000] px-5 py-2 rounded-xl font-medium transition-colors hidden md:block shadow-sm hover:shadow-md"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => {
                                    if (isAuthenticated) {
                                        setUserMenuOpen(!userMenuOpen);
                                    } else {
                                        navigate("/signin");
                                    }
                                }}
                            >
                                {isAuthenticated ? user?.name : "Sign In"}
                            </motion.button>{" "}
                            {/* User Menu Dropdown */}
                            {isAuthenticated && userMenuOpen && (
                                <div className="absolute right-0 top-16 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                                    {user?.role === "admin" && (
                                        <Link
                                            to="/admin"
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            onClick={() => setUserMenuOpen(false)}
                                        >
                                            Admin Dashboard
                                        </Link>
                                    )}{" "}
                                    {/* <button
                                        onClick={() => setUserMenuOpen(false)}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        Profile
                                    </button>{" "} */}
                                    <Link
                                        to="/my-orders"
                                        onClick={() => setUserMenuOpen(false)}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        My Orders
                                    </Link>
                                    <button
                                        onClick={logout}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        Sign Out
                                    </button>
                                </div>
                            )}{" "}
                            {/* Mobile menu button */}
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="md:hidden p-2 rounded-full hover:bg-gray-100 text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {mobileMenuOpen ? (
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    ) : (
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M4 6h16M4 12h16M4 18h16"
                                        />
                                    )}
                                </svg>
                            </button>
                        </motion.div>
                    </div>
                </div>
            </div>{" "}
            {/* Secondary Navigation Row with Categories - Notch Style */}
            <div className="flex justify-center -mt-0.5">
                <motion.div
                    className="py-1 px-1 rounded-b-xl justify-center transition-all duration-300 md:flex hidden bg-white"
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <div className="flex space-x-2 py-1 whitespace-nowrap">
                        {categories.slice(0, 6).map((category, idx) => (
                            <motion.a
                                key={idx}
                                href={`/subcategory/${category.slug}`}
                                className="text-sm font-medium px-2 py-1 rounded-full text-gray-600 hover:text-[#008000] hover:bg-gray-100 transition-colors"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3, delay: idx * 0.05 }}
                                whileHover={{ scale: 1.05 }}
                            >
                                {category.name}
                            </motion.a>
                        ))}

                    </div>
                </motion.div>
            </div>
            {/* Mobile Menu Drawer - Slides in from right */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        className="fixed inset-0 bg-black/50 z-40 md:hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setMobileMenuOpen(false)}
                    >
                        <motion.div
                            className="absolute right-0 top-0 h-full w-[80%] max-w-sm bg-white overflow-auto rounded-l-2xl"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center p-5 border-b">
                                <h2 className="font-bold text-lg text-[#008000]">Menu</h2>
                                <button
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="p-2 rounded-full hover:bg-gray-100"
                                >
                                    <svg
                                        className="w-5 h-5 text-gray-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>{" "}
                            {/* Search in Mobile Menu */}
                            <div className="p-5 border-b">
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search products..."
                                        className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#008000] focus:border-transparent"
                                        onClick={() => setSearchExpanded(true)}
                                    />
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                {searchQuery.trim().length >= 2 && (
                                    <div className="mt-2 max-h-64 overflow-y-auto bg-white rounded-lg border border-gray-200">
                                        {isSearching ? (
                                            <div className="p-4 text-center text-gray-500">Searching...</div>
                                        ) : searchResults.length > 0 ? (
                                            <div>
                                                {searchResults.map((product) => {
                                                    const productId = product._id || product.id;
                                                    return (
                                                        <Link
                                                            key={productId}
                                                            to={`/product/${productId}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleProductClick(productId);
                                                                setMobileMenuOpen(false);
                                                            }}
                                                            className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                                        >
                                                            {product.images && product.images[0] && (
                                                                <img
                                                                    src={typeof product.images[0] === 'string' ? product.images[0] : (product.images[0].url || product.images[0])}
                                                                    alt={product.name}
                                                                    className="w-12 h-12 object-cover rounded"
                                                                />
                                                            )}
                                                            <div className="flex-1 text-left">
                                                                <p className="font-medium text-gray-800">{product.name}</p>
                                                                {product.pricing && product.pricing[0] && (
                                                                    <p className="text-sm text-gray-600">
                                                                        ₹{product.pricing[0].basePrice || product.pricing[0].price}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="p-4 text-center text-gray-500">No products found</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {/* Navigation Links */}
                            <div className="p-5">
                                <ul className="space-y-3">
                                    <li>
                                        <a
                                            href="/"
                                            className="block text-base font-medium text-gray-800 hover:text-[#008000] transition-colors px-3 py-2 rounded-lg hover:bg-gray-50"
                                        >
                                            Home
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="#products"
                                            className="block text-base font-medium text-gray-800 hover:text-[#008000] transition-colors px-3 py-2 rounded-lg hover:bg-gray-50"
                                        >
                                            Products
                                        </a>
                                    </li>
                                    <li>
                                        <Link
                                            to="/templates"
                                            className="block text-base font-medium text-gray-800 hover:text-[#008000] transition-colors px-3 py-2 rounded-lg hover:bg-gray-50"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            Templates
                                        </Link>
                                    </li>
                                    <li>
                                        <a
                                            href="/about-us"
                                            className="block text-base font-medium text-gray-800 hover:text-[#008000] transition-colors px-3 py-2 rounded-lg hover:bg-gray-50"
                                        >
                                            Business
                                        </a>
                                    </li>
                                </ul>

                                {/* Categories Accordion */}
                                <div className="mt-6">
                                    <button
                                        className="flex justify-between items-center w-full text-left text-base font-medium text-gray-800 hover:text-[#008000] transition-colors px-3 py-2 rounded-lg hover:bg-gray-50"
                                        onClick={() => setMobileSubMenuOpen(!mobileSubMenuOpen)}
                                    >
                                        Categories
                                        <svg
                                            className={`w-5 h-5 transition-transform ${mobileSubMenuOpen ? "rotate-180" : ""
                                                }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </button>

                                    <AnimatePresence>
                                        {mobileSubMenuOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3 }}
                                                className="overflow-hidden"
                                            >
                                                <ul className="mt-2 ml-4 space-y-2 border-l-2 border-gray-100 pl-4">
                                                    {categories.map((category, idx) => (
                                                        <li key={idx}>
                                                            <a
                                                                href={`/category/${category.slug}`}
                                                                className="block text-base text-gray-600 hover:text-[#008000] transition-colors py-1.5 px-2 rounded-lg hover:bg-gray-50"
                                                            >
                                                                {category.name}
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                            {/* Action Buttons */}
                            <div className="p-5 border-t">
                                {isAuthenticated ? (
                                    <>
                                        <div className="mb-4 px-3 py-2 bg-gray-50 rounded-xl">
                                            <p className="text-xs text-gray-500 mb-1">Signed in as</p>
                                            <p className="text-base font-semibold text-gray-800">{user?.name || 'User'}</p>
                                        </div>
                                        {user?.role === "admin" && (
                                            <Link
                                                to="/admin"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="block w-full mb-3 px-4 py-2.5 bg-[#008000] text-white text-sm font-medium rounded-xl hover:bg-[#008000] transition-colors text-center"
                                            >
                                                Admin Dashboard
                                            </Link>
                                        )}
                                        <Link
                                            to="/my-orders"
                                            onClick={() => setMobileMenuOpen(false)}
                                            className="block w-full mb-3 px-4 py-2.5 bg-[#008000] text-white text-sm font-medium rounded-xl hover:bg-[#008000] transition-colors text-center"
                                        >
                                            My Orders
                                        </Link>
                                        <button
                                            onClick={() => {
                                                logout();
                                                setMobileMenuOpen(false);
                                            }}
                                            className="w-full px-4 py-2.5 bg-gray-100 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-200 transition-colors"
                                        >
                                            Sign Out
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link to="/signin" onClick={() => setMobileMenuOpen(false)}>
                                            <button className="w-full mb-3 px-4 py-2.5 bg-[#008000] text-white text-sm font-medium rounded-xl hover:bg-[#008000] transition-colors">
                                                Sign In
                                            </button>
                                        </Link>
                                        <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                                            <button className="w-full px-4 py-2.5 bg-gray-100 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-200 transition-colors">
                                                Create Account
                                            </button>
                                        </Link>
                                    </>
                                )}
                            </div>
                            {/* Contact Info */}
                            <div className="p-5 border-t">
                                <h3 className="text-sm font-medium text-gray-500 mb-2 px-2">Contact Us</h3>
                                <p className="text-sm text-[#008000] mb-1 px-2">+91 80801 84435</p>
                                <p className="text-sm text-[#008000] px-2">support@xpressprint.com</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            {/* Mobile Search Modal */}
            <AnimatePresence>
                {mobileSearchModalOpen && (
                    <motion.div
                        className="fixed inset-0 bg-black/50 z-50 md:hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => {
                            setMobileSearchModalOpen(false);
                            setSearchQuery("");
                            setSearchResults([]);
                        }}
                    >
                        <motion.div
                            className="absolute top-0 left-0 right-0 bg-white rounded-b-2xl shadow-xl"
                            initial={{ y: -100, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -100, opacity: 0 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="p-4 border-b">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="font-bold text-lg text-gray-800">Search Products</h2>
                                    <button
                                        onClick={() => {
                                            setMobileSearchModalOpen(false);
                                            setSearchQuery("");
                                            setSearchResults([]);
                                        }}
                                        className="p-2 rounded-full hover:bg-gray-100"
                                    >
                                        <svg
                                            className="w-5 h-5 text-gray-500"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search products..."
                                        className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#008000] focus:border-transparent"
                                        autoFocus
                                    />
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                            {searchQuery.trim().length >= 2 && (
                                <div className="max-h-96 overflow-y-auto">
                                    {isSearching ? (
                                        <div className="p-4 text-center text-gray-500">Searching...</div>
                                    ) : searchResults.length > 0 ? (
                                        <div>
                                            {searchResults.map((product) => {
                                                const productId = product._id || product.id;
                                                return (
                                                    <button
                                                        key={productId}
                                                        onClick={() => {
                                                            handleProductClick(productId);
                                                            setMobileSearchModalOpen(false);
                                                        }}
                                                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                                    >
                                                        {product.images && product.images[0] && (
                                                            <img
                                                                src={typeof product.images[0] === 'string' ? product.images[0] : (product.images[0].url || product.images[0])}
                                                                alt={product.name}
                                                                className="w-12 h-12 object-cover rounded"
                                                            />
                                                        )}
                                                        <div className="flex-1 text-left">
                                                            <p className="font-medium text-gray-800">{product.name}</p>
                                                            {product.pricing && product.pricing[0] && (
                                                                <p className="text-sm text-gray-600">
                                                                    ₹{product.pricing[0].basePrice || product.pricing[0].price}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="p-4 text-center text-gray-500">No products found</div>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            {location.pathname === "/" && <StatusMarquee />}
        </nav>
    );
};

export default Navbar;
